import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ChevronLeft, ChevronRight, Plus, X, Camera, Rocket, CheckCircle2, Lock, Clock } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, money, todayLocal, weekDates, prettyDate, prettyTs, RES_STYLES, fileToDataUrl } from "./api";

const HOURS = Array.from({ length: 16 }, (_, i) => `${String(i + 6).padStart(2, "0")}:${i % 2 === 0 ? "00" : "00"}`);
const START_TIMES = Array.from({ length: 25 }, (_, i) => `${String(6 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const FUEL_LEVELS = ["Full", "3/4", "1/2", "1/4", "Low"];

const emptyForm = { vehicle_id: "", customer_name: "", customer_email: "", customer_phone: "", date: todayLocal(), start_time: "10:00", duration_hours: 3 };

const Badge = ({ tone, children }) => (
  <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${tone}`}>{children}</span>
);

export default function Reservations() {
  const [params, setParams] = useSearchParams();
  const [weekAnchor, setWeekAnchor] = useState(todayLocal());
  const [reservations, setReservations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const days = useMemo(() => weekDates(weekAnchor), [weekAnchor]);

  const load = useCallback(async () => {
    try {
      const [{ data: res }, { data: veh }, { data: set }] = await Promise.all([
        api.get("/portal/reservations", { params: { date_from: days[0], date_to: days[6] } }),
        api.get("/portal/vehicles"),
        api.get("/portal/payment-links"),
      ]);
      setReservations(res);
      setVehicles(veh);
      setSettings(set);
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reservations");
      return [];
    }
  }, [days]);

  useEffect(() => {
    load().then((res) => {
      const focus = params.get("focus");
      if (focus) {
        const found = res.find((r) => r.reservation_id === focus);
        if (found) setActive(found);
      }
    });
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftWeek = (dir) => {
    const d = new Date(days[0] + "T12:00:00");
    d.setDate(d.getDate() + dir * 7);
    setWeekAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/reservations", form);
      toast.success(`Reservation ${data.reservation_id} created`);
      setShowCreate(false);
      setForm(emptyForm);
      await load();
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create reservation");
    } finally {
      setBusy(false);
    }
  };

  const refreshActive = async () => {
    const res = await load();
    if (active) {
      const found = res.find((r) => r.reservation_id === active.reservation_id);
      setActive(found || null);
    }
  };

  return (
    <PortalShell title="Reservation Calendar">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-1)} className="btn-ghost !px-3 !py-2" data-testid="week-prev" aria-label="Previous week"><ChevronLeft size={16} /></button>
          <p className="min-w-52 text-center font-display text-base font-bold text-offwhite" data-testid="week-label">
            {prettyDate(days[0])} – {prettyDate(days[6])}
          </p>
          <button onClick={() => shiftWeek(1)} className="btn-ghost !px-3 !py-2" data-testid="week-next" aria-label="Next week"><ChevronRight size={16} /></button>
          <button onClick={() => setWeekAnchor(todayLocal())} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="week-today">This week</button>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="new-reservation-btn"><Plus size={16} /> New Reservation</button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7" data-testid="calendar-grid">
        {days.map((day) => {
          const todays = reservations.filter((r) => r.date === day);
          const isToday = day === todayLocal();
          return (
            <div key={day} className={`min-h-36 rounded-xl border p-3 ${isToday ? "border-aqua/50 bg-aqua/[0.05]" : "border-black/[0.08] bg-white/60"}`} data-testid={`cal-day-${day}`}>
              <p className={`mb-2 text-xs font-bold uppercase tracking-widest ${isToday ? "text-aqua" : "text-muted"}`}>{prettyDate(day)}</p>
              <div className="space-y-2">
                {todays.length === 0 && <p className="text-[11px] text-muted/60">Open</p>}
                {todays.map((r) => (
                  <button
                    key={r.reservation_id}
                    onClick={() => { setActive(r); setParams({ focus: r.reservation_id }); }}
                    data-testid={`cal-res-${r.reservation_id}`}
                    className={`w-full rounded-lg border p-2.5 text-left transition-transform hover:-translate-y-0.5 ${RES_STYLES[r.status]}`}
                  >
                    <p className="text-xs font-bold">{r.start_time} · {r.duration_hours + r.extensions_hours}h</p>
                    <p className="truncate text-[11px]">{r.vehicle_name}</p>
                    <p className="truncate text-[11px] opacity-80">{r.customer.name}</p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="card-line max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="create-reservation-modal">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-offwhite">New Reservation</h2>
              <button onClick={() => setShowCreate(false)} aria-label="Close" data-testid="create-close"><X size={20} className="text-muted" /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="field-label" htmlFor="cr-vehicle">Assigned jet car</label>
                <select id="cr-vehicle" className="field" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="create-vehicle">
                  <option value="">Select vehicle…</option>
                  {vehicles.filter((v) => !["maintenance", "out_of_service"].includes(v.status)).map((v) => (
                    <option key={v.vehicle_id} value={v.vehicle_id} className="bg-white">{v.name} · {v.color}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2"><label className="field-label" htmlFor="cr-name">Customer name</label><input id="cr-name" className="field" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="create-name" /></div>
              <div><label className="field-label" htmlFor="cr-email">Customer email</label><input id="cr-email" type="email" className="field" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="create-email" /></div>
              <div><label className="field-label" htmlFor="cr-phone">Customer phone</label><input id="cr-phone" className="field" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="create-phone" /></div>
              <div><label className="field-label" htmlFor="cr-date">Rental date</label><input id="cr-date" type="date" className="field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="create-date" /></div>
              <div><label className="field-label" htmlFor="cr-start">Start time</label>
                <select id="cr-start" className="field" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="create-start">
                  {START_TIMES.map((t) => <option key={t} value={t} className="bg-white">{t}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="field-label">Duration (minimum 3 hours)</label>
                <div className="grid grid-cols-3 gap-2">
                  {[3, 4, 5].map((h) => (
                    <button key={h} onClick={() => setForm({ ...form, duration_hours: h })} data-testid={`create-duration-${h}`}
                      className={`rounded-lg border py-3 font-display text-sm font-bold transition-colors ${form.duration_hours === h ? "border-aqua bg-aqua/10 text-aqua" : "border-black/15 text-muted"}`}>
                      {h} hours
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2 rounded-xl border border-black/[0.08] bg-ink/50 p-4 text-center">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted">Estimated total</p>
                <p className="mt-1 font-display text-2xl font-extrabold text-offwhite" data-testid="create-price">{money(form.duration_hours * (settings?.hourly_rate ?? 350))}</p>
              </div>
            </div>
            <button onClick={create} disabled={busy || !form.vehicle_id || !form.customer_name || !form.customer_email || !form.customer_phone} className="btn-primary mt-6 w-full disabled:opacity-50" data-testid="create-submit">
              {busy ? "Creating…" : "Create Reservation"}
            </button>
          </div>
        </div>
      )}

      {active && <ReservationDrawer res={active} settings={settings} onClose={() => { setActive(null); setParams({}); }} onChanged={refreshActive} />}
    </PortalShell>
  );
}

function ReservationDrawer({ res, settings, onClose, onChanged }) {
  const [txn, setTxn] = useState(res.stripe_transaction_id || "");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const c = res.checkin || {};
  const co = res.checkout || {};
  const launched = res.status === "launched";
  const closed = ["completed", "cancelled"].includes(res.status);
  const bookingLink = `${window.location.origin}/checkout/${res.reservation_id}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingLink);
      toast.success("Checkout link copied");
    } catch {
      toast.error("Copy failed — long-press the link instead");
    }
  };
  const openCheckout = () => act(async () => {
    const { data } = await api.post(`/portal/reservations/${res.reservation_id}/checkout-session`, { origin: window.location.origin });
    window.open(data.url, "_blank", "noopener");
  }, null);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const checkinField = (field, value) => act(() => api.post(`/portal/reservations/${res.reservation_id}/checkin`, { [field]: value }), null);

  const addPhotos = async (files, target) => {
    const urls = await Promise.all(Array.from(files).slice(0, 4).map(fileToDataUrl));
    const existing = target === "checkin" ? (c.photos || []) : photos;
    const next = [...existing, ...urls].slice(0, 6);
    if (target === "checkin") await checkinField("photos", next);
    else setPhotos(next);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="h-full w-full max-w-xl overflow-y-auto bg-ink p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="reservation-drawer">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs text-aqua">{res.reservation_id}</p>
            <h2 className="mt-1 font-display text-2xl font-extrabold text-offwhite">{res.customer.name}</h2>
            <p className="mt-1 text-sm text-muted">{prettyDate(res.date)} · {res.start_time} – {res.end_time} · {res.duration_hours + res.extensions_hours}h · {res.vehicle_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={RES_STYLES[res.status]}>{res.status.replace("_", " ")}</Badge>
            <button onClick={onClose} aria-label="Close" data-testid="drawer-close"><X size={22} className="text-muted" /></button>
          </div>
        </div>

        <section className="card-line mb-4 rounded-xl p-5">
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Customer</h3>
          <p className="text-sm text-offwhite">{res.customer.email} · {res.customer.phone}</p>
          <p className="mt-2 text-sm text-muted">Total: <strong className="text-offwhite">{money(res.price)}</strong> {res.extensions_hours > 0 && <span className="text-xs">(includes {res.extensions_hours}h extension)</span>}</p>
        </section>

        <section className="card-line mb-4 rounded-xl p-5" data-testid="payment-section">
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Payment</h3>
          <div className="mb-3 flex items-center gap-2">
            <Badge tone={res.payment_status === "paid" ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-orange-500/40 bg-orange-400/10 text-orange-700"}>
              {res.payment_status}
            </Badge>
            {res.stripe_transaction_id && <span className="font-mono text-[11px] text-muted">{res.stripe_transaction_id}</span>}
          </div>
          {!closed && (
            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold text-offwhite">Customer checkout link (upsells offered here)</p>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-white p-2.5" data-testid="payment-qr">
                  <QRCodeSVG value={bookingLink} size={92} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="break-all font-mono text-[11px] text-muted" data-testid="checkout-link-text">{bookingLink}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button onClick={copyLink} className="btn-ghost !px-3 !py-1.5 !text-[11px]" data-testid="copy-checkout-link">Copy link</button>
                    <button onClick={openCheckout} disabled={busy} className="btn-primary !px-3 !py-1.5 !text-[11px]" data-testid="staff-checkout-btn">Staff checkout</button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-muted/70">Customer picks approved upsells + extra hours, then pays one secure XE checkout. Payment confirms automatically via webhook.</p>
                </div>
              </div>
            </div>
          )}
          {res.payment_status !== "paid" && !closed && (
            <div className="flex gap-2">
              <input className="field flex-1" placeholder="Stripe transaction ID (e.g. pi_…)" value={txn} onChange={(e) => setTxn(e.target.value)} data-testid="txn-input" />
              <button disabled={busy || txn.trim().length < 3} onClick={() => act(() => api.post(`/portal/reservations/${res.reservation_id}/payment`, { stripe_transaction_id: txn.trim() }), "Payment confirmed")} className="btn-primary !px-4 !py-2 !text-xs disabled:opacity-50" data-testid="confirm-payment-btn">
                Confirm
              </button>
            </div>
          )}
          {!closed && ["upcoming", "checked_in", "launched"].includes(res.status) && (
            <div className="mt-4 border-t border-black/[0.08] pt-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted"><Clock size={12} /> Extend rental (subject to availability)</p>
              <div className="flex gap-2">
                {[1, 2, 3].map((h) => (
                  <button key={h} disabled={busy} onClick={() => act(() => api.post(`/portal/reservations/${res.reservation_id}/extend`, { hours: h }), `Extended +${h}h`)} className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`extend-${h}h`}>+{h}h</button>
                ))}
              </div>
              {res.extensions_hours > 0 && (
                <p className="mt-2 text-[11px] text-muted/70">Extension payment is collected through the customer checkout link above at the approved hourly rate.</p>
              )}
            </div>
          )}
        </section>

        {!closed && (
          <section className="card-line mb-4 rounded-xl p-5" data-testid="checkin-section">
            <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Check-In</h3>
            <div className="space-y-2.5">
              {[
                ["id_verified", "Identification verified"],
                ["waiver_completed", "Waiver completed & signed"],
                ["safety_orientation", "Safety orientation completed"],
              ].map(([field, label]) => (
                <label key={field} className="flex cursor-pointer items-center gap-3 text-sm text-offwhite">
                  <input type="checkbox" checked={!!c[field]} disabled={busy} onChange={(e) => checkinField(field, e.target.checked)} className="h-5 w-5 accent-[#00A8A2]" data-testid={`checkin-${field}`} />
                  {label}
                  {c.step_timestamps?.[field] && <span className="ml-auto text-[10px] text-muted">{prettyTs(c.step_timestamps[field])}</span>}
                </label>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="field-label" htmlFor="ci-fuel">Fuel level</label>
                  <select id="ci-fuel" className="field" value={c.fuel_level || ""} disabled={busy} onChange={(e) => checkinField("fuel_level", e.target.value)} data-testid="checkin-fuel">
                    <option value="">Select…</option>
                    {FUEL_LEVELS.map((f) => <option key={f} value={f} className="bg-white">{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="ci-hours">Beginning engine hours</label>
                  <input id="ci-hours" type="number" step="0.1" className="field" defaultValue={c.beginning_engine_hours ?? ""} disabled={busy} onBlur={(e) => e.target.value !== "" && checkinField("beginning_engine_hours", parseFloat(e.target.value))} data-testid="checkin-engine-hours" />
                </div>
              </div>
              <div>
                <label className="field-label" htmlFor="ci-damage">Existing-damage notes</label>
                <textarea id="ci-damage" rows={2} className="field resize-none" defaultValue={c.damage_notes || ""} disabled={busy} onBlur={(e) => checkinField("damage_notes", e.target.value)} data-testid="checkin-damage" />
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted"><Camera size={13} /> Pre-rental photographs ({(c.photos || []).length}/6)</label>
                <input type="file" accept="image/*" multiple disabled={busy} onChange={(e) => e.target.files.length && addPhotos(e.target.files, "checkin")} className="block w-full text-xs text-muted file:mr-3 file:rounded-full file:border-0 file:bg-aqua file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#052726]" data-testid="checkin-photos" />
                <div className="mt-2 flex flex-wrap gap-2">
                  {(c.photos || []).map((p, i) => <img key={i} src={p} alt={`Pre-rental ${i + 1}`} className="h-14 w-14 rounded-lg border border-black/10 object-cover" />)}
                </div>
              </div>
            </div>
            <div className="mt-5 border-t border-black/[0.08] pt-4">
              <button
                disabled={busy || !res.launch_ready || launched}
                onClick={() => act(() => api.post(`/portal/reservations/${res.reservation_id}/launch`), "Launch authorized")}
                className="btn-primary w-full disabled:opacity-40"
                data-testid="launch-btn"
              >
                {launched ? <><Rocket size={15} /> Launched {prettyTs(c.actual_launch_time)}</> : res.launch_ready ? <><Rocket size={15} /> Launch Authorized</> : <><Lock size={15} /> Launch Locked</>}
              </button>
              {!res.launch_ready && !launched && (
                <p className="mt-2 text-center text-[11px] text-muted" data-testid="launch-requirements">Requires confirmed payment + all check-in steps</p>
              )}
            </div>
          </section>
        )}

        {launched && <CheckoutForm res={res} busy={busy} act={act} photos={photos} setPhotos={setPhotos} addPhotos={addPhotos} />}

        {closed && co.completed_at && (
          <section className="card-line mb-4 rounded-xl p-5" data-testid="checkout-summary">
            <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Checkout Summary</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <p className="text-muted">Returned: <span className="text-offwhite">{prettyTs(co.actual_return)}</span></p>
              <p className="text-muted">Late: <span className={co.late_return ? "font-bold text-red-600" : "text-emerald-700"}>{co.late_return ? "Yes" : "No"}</span></p>
              <p className="text-muted">End engine hours: <span className="text-offwhite">{co.ending_engine_hours}</span></p>
              <p className="text-muted">Fuel: <span className="text-offwhite">{co.fuel_level}</span></p>
              <p className="text-muted">Damage: <span className="text-offwhite">{co.damage_status}</span></p>
              <p className="text-muted">Incident: <span className="text-offwhite">{co.incident_status}</span></p>
            </div>
            {co.additional_time_payment_id && <p className="mt-2 font-mono text-[11px] text-muted">Additional time: {co.additional_time_payment_id}</p>}
            {co.notes && <p className="mt-2 text-sm text-muted">{co.notes}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              {(co.photos || []).map((p, i) => <img key={i} src={p} alt={`Post-rental ${i + 1}`} className="h-14 w-14 rounded-lg border border-black/10 object-cover" />)}
            </div>
          </section>
        )}

        <section className="card-line mb-4 rounded-xl p-5" data-testid="audit-trail">
          <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Audit Trail</h3>
          <div className="space-y-2">
            {(res.audit || []).slice().reverse().map((a, i) => (
              <p key={i} className="flex items-start gap-2 text-xs text-muted">
                <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-aqua" />
                <span><span className="text-offwhite">{a.event}</span> — {prettyTs(a.at)} · {a.by}</span>
              </p>
            ))}
          </div>
        </section>

        {!closed && (
          <button disabled={busy} onClick={() => act(() => api.post(`/portal/reservations/${res.reservation_id}/cancel`), "Reservation cancelled")} className="w-full rounded-full border border-red-500/40 py-3 text-xs font-bold uppercase tracking-widest text-red-600 transition-colors hover:bg-red-500/10" data-testid="cancel-reservation-btn">
            Cancel Reservation
          </button>
        )}
      </div>
    </div>
  );
}

function CheckoutForm({ res, busy, act, photos, setPhotos, addPhotos }) {
  const [form, setForm] = useState({
    ending_engine_hours: "",
    fuel_level: "",
    additional_time_payment_id: "",
    damage_status: "none",
    incident_status: "none",
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ready = form.ending_engine_hours !== "" && form.fuel_level && photos.length >= 1;

  return (
    <section className="card-line mb-4 rounded-xl border-aqua/40 p-5" data-testid="checkout-section">
      <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Checkout</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="field-label" htmlFor="co-hours">Ending engine hours</label>
          <input id="co-hours" type="number" step="0.1" className="field" value={form.ending_engine_hours} onChange={(e) => set("ending_engine_hours", e.target.value)} data-testid="checkout-engine-hours" />
        </div>
        <div>
          <label className="field-label" htmlFor="co-fuel">Fuel level</label>
          <select id="co-fuel" className="field" value={form.fuel_level} onChange={(e) => set("fuel_level", e.target.value)} data-testid="checkout-fuel">
            <option value="">Select…</option>
            {FUEL_LEVELS.map((f) => <option key={f} value={f} className="bg-white">{f}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="field-label" htmlFor="co-addpay">Additional-time payment ID (if extended)</label>
          <input id="co-addpay" className="field" value={form.additional_time_payment_id} onChange={(e) => set("additional_time_payment_id", e.target.value)} data-testid="checkout-addpay" />
        </div>
        <div>
          <label className="field-label" htmlFor="co-damage">Damage status</label>
          <select id="co-damage" className="field" value={form.damage_status} onChange={(e) => set("damage_status", e.target.value)} data-testid="checkout-damage">
            {["none", "minor", "major"].map((d) => <option key={d} value={d} className="bg-white">{d}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="co-incident">Incident status</label>
          <select id="co-incident" className="field" value={form.incident_status} onChange={(e) => set("incident_status", e.target.value)} data-testid="checkout-incident">
            {["none", "reported"].map((d) => <option key={d} value={d} className="bg-white">{d}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="field-label" htmlFor="co-notes">Notes</label>
          <textarea id="co-notes" rows={2} className="field resize-none" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="checkout-notes" />
        </div>
        <div className="col-span-2">
          <label className="mb-1.5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted"><Camera size={13} /> Post-rental photographs ({photos.length}/6)</label>
          <input type="file" accept="image/*" multiple onChange={(e) => e.target.files.length && addPhotos(e.target.files, "checkout")} className="block w-full text-xs text-muted file:mr-3 file:rounded-full file:border-0 file:bg-aqua file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#052726]" data-testid="checkout-photos" />
          <div className="mt-2 flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p} alt={`Post-rental ${i + 1}`} className="h-14 w-14 rounded-lg border border-black/10 object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-red-500 text-white" aria-label="Remove photo"><X size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <button
        disabled={busy || !ready}
        onClick={() => act(() => api.post(`/portal/reservations/${res.reservation_id}/checkout`, {
          actual_return: new Date().toISOString(),
          ending_engine_hours: parseFloat(form.ending_engine_hours),
          fuel_level: form.fuel_level,
          additional_time_payment_id: form.additional_time_payment_id,
          damage_status: form.damage_status,
          incident_status: form.incident_status,
          notes: form.notes,
          photos,
        }), "Checkout complete — vehicle returned to available")}
        className="btn-primary mt-5 w-full disabled:opacity-40"
        data-testid="checkout-submit"
      >
        Complete Checkout
      </button>
      {!ready && <p className="mt-2 text-center text-[11px] text-muted">Requires engine hours, fuel level and at least one photo</p>}
    </section>
  );
}
